import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSecurePrivacy } from "@/contexts/SecurePrivacyContext";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent || "") && !window.MSStream;
const isStandalone = () => {
    try { return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true; }
    catch { return false; }
};

/**
 * Screenshot-detected UX. Shows an amber Sonner toast + logs the event
 * to /api/audit/privacy-event. On iOS Safari (not yet installed as a
 * PWA), also shows a persistent home-screen install prompt so future
 * screenshots trigger the native `UIScreen.capturedDidChangeNotification`
 * capture-shield behaviour that only the installed shell can access.
 */
export default function ScreenshotToast() {
    const { user } = useAuth();
    const { log_events } = useSecurePrivacy();
    const [showInstall, setShowInstall] = useState(false);

    useEffect(() => {
        if (!user?.id) return;
        const handler = async (e) => {
            const platform = e.detail?.platform || (isIOS() ? "ios" : "web");
            toast.warning("Screenshot detected", {
                description: "The captured image may contain private financial information. Please handle it accordingly.",
                icon: <AlertTriangle size={16} />,
                duration: 8000, id: "vault-screenshot-toast",
            });
            if (log_events !== false) {
                try {
                    await api.post("/audit/privacy-event", {
                        event_type: "screenshot_detected", platform,
                        meta: { at: e.detail?.at || Date.now() },
                    });
                } catch { /* best-effort */ }
            }
            if (isIOS() && !isStandalone() && !sessionStorage.getItem("vault.ios.install.dismissed")) {
                setShowInstall(true);
            }
        };
        window.addEventListener("vault:screenshot-detected", handler);
        return () => window.removeEventListener("vault:screenshot-detected", handler);
    }, [user?.id, log_events]);

    if (!showInstall) return null;

    const dismiss = () => {
        try { sessionStorage.setItem("vault.ios.install.dismissed", "1"); } catch { /* private mode */ }
        setShowInstall(false);
    };

    return (
        <div
            role="dialog" aria-label="Install VAULT for stronger privacy"
            data-testid="ios-install-prompt"
            style={{
                position: "fixed", bottom: 16, left: 16, right: 16, zIndex: 100,
                background: "linear-gradient(180deg,#101827,#0B1122)",
                border: "1px solid rgba(198,168,107,0.4)",
                borderRadius: 16, padding: "16px 18px", color: "#F6F3EC",
                boxShadow: "0 20px 40px -10px rgba(0,0,0,0.5)",
                maxWidth: 460, marginLeft: "auto", marginRight: "auto",
            }}
        >
            <div style={{ fontSize: 10.5, letterSpacing: "0.2em", fontWeight: 700, color: "#C6A86B", textTransform: "uppercase", marginBottom: 6 }}>
                Stronger protection available
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>
                Install VAULT to your Home Screen.
            </div>
            <div style={{ marginTop: 6, fontSize: 12.5, color: "rgba(246,243,236,0.7)", lineHeight: 1.5 }}>
                On iOS, installing VAULT unlocks native capture protection — the screen is
                automatically covered during screen recording and mirroring. Tap the <b>Share</b> icon
                in Safari, then <b>Add to Home Screen</b>.
            </div>
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
                <a href="/privacy-centre" data-testid="ios-install-learn"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#C6A86B", fontSize: 12.5, fontWeight: 600, textDecoration: "none" }}>
                    Learn more <ArrowRight size={12} />
                </a>
                <button onClick={dismiss} data-testid="ios-install-dismiss"
                    style={{ marginLeft: "auto", background: "none", border: "none", color: "rgba(246,243,236,0.55)", fontSize: 12, cursor: "pointer" }}>
                    Not now
                </button>
            </div>
        </div>
    );
}
